import { DataSnapshot } from '../api/firebase';
import { OrderPerson, StaffMember, Student, UserProfile } from '../types';

/** Single student or staff row in profile (matches checkProfile rules for that person). */
export function isOrderPersonEntryComplete(person: OrderPerson | undefined, isStudent: boolean): boolean {
    if (!person) {
        return false;
    }
    const firstName = (person.firstName ?? '').trim();
    const lastName = (person.lastName ?? '').trim();
    if (!firstName || !lastName) {
        return false;
    }
    if (isStudent) {
        const classroom = (person as Student).classroom;
        return !!classroom && classroom !== 'unknown';
    }
    return !!(person as StaffMember).jobRole;
}

function validateEmail(email) {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase().trim());
}

export const PROFILE_VALIDATION_SUCCESS = 0;
export const PROFILE_VALIDATION_FNAME = 1;
export const PROFILE_VALIDATION_LNAME = 2;
export const PROFILE_VALIDATION_EMAIL = 3;
export const PROFILE_VALIDATION_MOBILE = 4;
export const PROFILE_VALIDATION_ROLE = 5;
export const PROFILE_VALIDATION_NO_PEOPLE = 6;
export const PROFILE_VALIDATION_STUDENT_FNAME = 7;
export const PROFILE_VALIDATION_STUDENT_LNAME = 8;
export const PROFILE_VALIDATION_STUDENT_CLASSROOM = 9;
export const PROFILE_VALIDATION_STAFF_JOB_ROLE = 10;
export const PROFILE_VALIDATION_EMAIL_VERIFIED = 11;

export const PROFILE_STUDENTS_SECTION_ID = 'profile-students-section';
export const PROFILE_PERSONNEL_BLOCK_ID = 'profile-personnel-block';
export const PROFILE_EMAIL_VERIFICATION_STATUS_ALERT_ID = 'profile-email-verification-status-alert';

export function profileStudentRowAnchorId(personKey: string): string {
    return `profile-student-row-${personKey}`;
}

export function isProfileStudentRowFocusId(focusId: string | null | undefined): boolean {
    return !!focusId && focusId.startsWith('profile-student-row-');
}

export function profileFieldAnchorId(field: 'firstName' | 'lastName' | 'email' | 'mobile'): string {
    // Avoid id suffix "-mobile" (some content blockers hide matching nodes).
    if (field === 'mobile') {
        return 'profile-personal-phone';
    }
    return `profile-field-${field}`;
}

/** Listed rows only — incomplete student/personnel drafts are removed, not shown in the account list. */
function findProfileValidationPersonRowId(students: DataSnapshot<OrderPerson>[] | undefined, code: number): string | null {
    if (!students?.length) {
        return null;
    }
    for (const studentSnap of Object.values(students)) {
        const person = studentSnap?.val() as OrderPerson | undefined;
        const key = studentSnap?.key;
        if (!key || !person) {
            continue;
        }
        if (!person.firstName && code === PROFILE_VALIDATION_STUDENT_FNAME) {
            return profileStudentRowAnchorId(key);
        }
        if (person.firstName && !person.lastName && code === PROFILE_VALIDATION_STUDENT_LNAME) {
            return profileStudentRowAnchorId(key);
        }
        const isStudent = !!person.student;
        if (
            isStudent
            && code === PROFILE_VALIDATION_STUDENT_CLASSROOM
            && person.firstName
            && person.lastName
            && (!(person as Student).classroom || (person as Student).classroom === 'unknown')
        ) {
            return profileStudentRowAnchorId(key);
        }
        const isStaff = !!person.personnel;
        if (isStaff && code === PROFILE_VALIDATION_STAFF_JOB_ROLE && person.firstName && person.lastName && !(person as StaffMember).jobRole) {
            return profileStudentRowAnchorId(key);
        }
    }
    return null;
}

/** DOM id to scroll/focus when `checkProfile` returns this code. */
export function profileValidationFocusTargetId(code: number, students?: DataSnapshot<OrderPerson>[]): string | null {
    const personRowId = findProfileValidationPersonRowId(students, code);
    if (personRowId) {
        return personRowId;
    }
    switch (code) {
        case PROFILE_VALIDATION_FNAME:
            return profileFieldAnchorId('firstName');
        case PROFILE_VALIDATION_LNAME:
            return profileFieldAnchorId('lastName');
        case PROFILE_VALIDATION_EMAIL:
        case PROFILE_VALIDATION_EMAIL_VERIFIED:
            return profileFieldAnchorId('email');
        case PROFILE_VALIDATION_MOBILE:
            return profileFieldAnchorId('mobile');
        case PROFILE_VALIDATION_NO_PEOPLE:
        case PROFILE_VALIDATION_STUDENT_FNAME:
        case PROFILE_VALIDATION_STUDENT_LNAME:
        case PROFILE_VALIDATION_STUDENT_CLASSROOM:
            return PROFILE_STUDENTS_SECTION_ID;
        case PROFILE_VALIDATION_STAFF_JOB_ROLE:
            return PROFILE_PERSONNEL_BLOCK_ID;
        default:
            return null;
    }
}

export const checkProfile = (profile: Partial<UserProfile>, students: DataSnapshot<OrderPerson>[], emailVerificationData?: any): number => {

    if (profile === undefined)
        return undefined;

    profile = profile ?? {};

    if (!profile.firstName)
        return PROFILE_VALIDATION_FNAME;
    else if (!profile.lastName)
        return PROFILE_VALIDATION_LNAME;
    else if (!(profile.email ?? '').trim() || !validateEmail(profile.email))
        return PROFILE_VALIDATION_EMAIL;
    else if (emailVerificationData && !emailVerificationData.verified)
        return PROFILE_VALIDATION_EMAIL_VERIFIED;
    else if (!(profile.mobile ?? '').trim())
        return PROFILE_VALIDATION_MOBILE;

    if (students === undefined)
        return undefined;

    // Check if there are any people (students or personnel) at all
    const validPeople = students.filter(student => {
        const person = student?.val() as OrderPerson;
        return person?.firstName && person?.lastName;
    });

    if (validPeople.length <= 0)
        return PROFILE_VALIDATION_NO_PEOPLE;

    for (let student of Object.values(students)) {
        const person = student?.val() as OrderPerson;
        if (!person?.firstName)
            return PROFILE_VALIDATION_STUDENT_FNAME;
        else if (!person?.lastName)
            return PROFILE_VALIDATION_STUDENT_LNAME;

        // Only check classroom for students, not staff members
        const isStudent = !!person?.student;
        if (isStudent && (!(person as Student)?.classroom || (person as Student)?.classroom === 'unknown'))
            return PROFILE_VALIDATION_STUDENT_CLASSROOM;

        // Check job role for staff members
        const isStaff = !!person?.personnel;
        if (isStaff && !(person as any)?.jobRole) {
            return PROFILE_VALIDATION_STAFF_JOB_ROLE;
        }
    }

    return PROFILE_VALIDATION_SUCCESS;
};